/* Shopping cart overview with a trust panel, editable items, coupon guidance, and checkout actions.
   Updated: Added DataFast cart_viewed goal on page load with items.
   Updated: Shows delivery costs by default based on cart contents (no address required).
   Updated: Trust panel mentions choosing your own delivery day. */

import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Check, Minus, Plus, Trash2, Truck } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactBar from '../components/ContactBar';
import PageBreadcrumb from '../components/PageBreadcrumb';
import { useCart } from '../contexts/CartContext';
import { trackGoal } from '@/lib/datafast';
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

interface DeliverySettings {
  tuinplanten_fee_cents: number;
  haagplanten_fee_cents: number;
  free_from_amount_cents: number;
  tuinplanten_minimum_cents: number;
  bomen_fee_cents?: number;
}

export default function CartOverviewPage() {
  const { items, total, totalDiscount, updateQuantity, removeItem } = useCart();
  const navigate = useNavigate();
  const formatPrice = (price: number) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(price);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [deliverySettings, setDeliverySettings] = useState<DeliverySettings | null>(null);

  useEffect(() => {
    supabase
      .from('delivery_settings')
      .select('setting_value')
      .eq('setting_key', 'category_delivery_fees')
      .maybeSingle()
      .then(({ data }) => {
        if (data?.setting_value) setDeliverySettings(data.setting_value);
      });
  }, []);

  const hasTuinplanten = items.some(item => item.category_slug === 'tuinplanten');
  const hasHaagplanten = items.some(item => item.category_slug === 'haagplanten');
  const hasBomen = items.some(item => item.category_slug === 'bomen');

  const deliveryFee = deliverySettings
    ? (total >= deliverySettings.free_from_amount_cents / 100
        ? 0
        : (hasTuinplanten ? deliverySettings.tuinplanten_fee_cents / 100 : 0)
          + (hasHaagplanten ? deliverySettings.haagplanten_fee_cents / 100 : 0)
          + (hasBomen ? (deliverySettings.bomen_fee_cents || deliverySettings.haagplanten_fee_cents) / 100 : 0))
    : 0;
  const transportIsFree = deliverySettings && total >= deliverySettings.free_from_amount_cents / 100;
  const grandTotal = total + deliveryFee;

  const startEdit = (item: { id: string; quantity: number }) => {
    setEditingId(item.id);
    setEditValue(String(item.quantity));
  };

  const commitEdit = (itemId: string) => {
    const parsed = parseInt(editValue, 10);
    if (!isNaN(parsed) && parsed > 0) {
      updateQuantity(itemId, parsed);
    }
    setEditingId(null);
    setEditValue('');
  };

  useEffect(() => {
    if (items.length > 0) trackGoal('cart_viewed', { item_count: items.length, total });
  }, [items.length, total]);

  return (
    <>
      <Helmet>
        <title>Winkelwagen | Buitn Planten</title>
        <meta name="description" content="Bekijk je winkelwagen en ga eenvoudig verder naar veilig afrekenen bij Buitn Planten." />
        <link rel="canonical" href="https://buitnplanten.nl/winkelwagen" />
      </Helmet>
      <ContactBar />
      <Header />
      <main className="min-h-[70vh] bg-[#fcfaf6] pb-20 pt-7 text-[#1e2b22]">
        <div className="mx-auto max-w-[1240px] px-5 lg:px-8">
          <PageBreadcrumb items={[{ label: 'Home', href: '/' }, { label: 'Winkelwagen' }]} />

          {items.length === 0 ? (
            <div className="mx-auto max-w-xl py-24 text-center">
              <h1 className="text-4xl tracking-[-0.035em]">Je winkelwagen is leeg</h1>
              <p className="mt-3 text-sm leading-6 text-[#5a6552]">Voeg mooie planten toe om jouw tuin compleet te maken.</p>
              <button onClick={() => navigate('/assortiment')} className="mt-7 inline-flex min-h-12 items-center gap-2 rounded-md bg-[#1e2b22] px-7 text-sm font-semibold text-white transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-[#5a6552]">
                Bekijk assortiment <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(250px,0.38fr)_minmax(0,1fr)] lg:gap-12">
              <aside className="h-fit rounded-xl bg-white p-6 shadow-[0_14px_35px_rgba(30,43,34,0.05)] ring-1 ring-[#e7e1d6] sm:p-8 lg:sticky lg:top-6">
                <p className="text-xs uppercase tracking-[0.18em] text-[#5a6552]">Waarom Buitn Planten</p>
                <h1 className="mt-3 text-3xl tracking-[-0.035em] sm:text-4xl">Goed voorbereid naar je tuin</h1>
                <div className="mt-7 space-y-4">
                  {[
                    'Kies zelf je bezorgdag',
                    'Zorgvuldig en snel bezorgd in heel Nederland',
                    'Topkwaliteit, rechtstreeks van de kwekerij',
                    'Altijd vers geleverd voor een sterke start',
                    'Persoonlijk advies wanneer je dat nodig hebt',
                  ].map((benefit) => (
                    <div key={benefit} className="flex items-start gap-3 text-sm leading-5 text-[#5a6552]">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-[#91b56d] text-[#6d9854]"><Check className="h-3 w-3" strokeWidth={2.5} /></span>
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
              </aside>

              <section className="min-w-0">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-[#5a6552]">Jouw selectie</p>
                    <h2 className="mt-2 text-4xl font-semibold tracking-[-0.045em] sm:text-5xl">Winkelwagen</h2>
                  </div>
                  <button onClick={() => navigate('/winkelwagen/afrekenen')} className="inline-flex min-h-12 items-center gap-2 rounded-full bg-[#1e2b22] px-7 text-sm font-semibold text-white shadow-[0_8px_20px_rgba(30,43,34,0.15)] transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-[#5a6552]">Afrekenen <ArrowRight className="h-4 w-4" /></button>
                </div>

                <div className="mt-8 hidden grid-cols-[minmax(0,1fr)_150px_110px] gap-5 border-b border-[#e7e1d6] px-4 pb-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#5a6552] sm:grid">
                  <span>Artikel</span><span className="text-center">Aantal</span><span className="text-right">Totaal</span>
                </div>

                <div className="divide-y divide-[#e7e1d6] border-y border-[#e7e1d6]">
                  {items.map((item) => (
                    <article key={item.id} className="grid gap-4 px-0 py-5 sm:grid-cols-[minmax(0,1fr)_150px_110px] sm:items-center sm:gap-5 sm:px-4">
                      <div className="flex min-w-0 gap-4">
                        <div className="h-20 w-16 shrink-0 overflow-hidden rounded-lg bg-white ring-1 ring-[#e7e1d6] sm:h-24 sm:w-20">
                          {item.image_url ? <img src={item.image_url} alt={item.product_name} className="h-full w-full object-cover" /> : <div className="h-full w-full bg-[#f3efe7]" />}
                        </div>
                        <div className="min-w-0 pt-1">
                          <h3 className="text-sm font-semibold leading-5 text-[#1e2b22] sm:text-base">{item.product_name}{item.variant_size ? ` ${item.variant_size}` : ''}</h3>
                          {item.variant_type && <p className="mt-1 text-xs text-[#6d9854]">{item.variant_type}</p>}
                          <p className="mt-1 text-xs text-[#8b867d]">{formatPrice(item.unit_price)} per stuk</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:justify-center">
                        <div className="flex items-center rounded-md border border-[#d8d2c8] bg-white">
                          <button onClick={() => updateQuantity(item.id, item.quantity - 1)} aria-label="Aantal verminderen" className="p-2 text-[#5a6552] transition-colors hover:text-[#1e2b22]"><Minus className="h-3.5 w-3.5" /></button>
                          {editingId === item.id ? (
                            <input
                              type="number"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onBlur={() => commitEdit(item.id)}
                              onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(item.id); if (e.key === 'Escape') { setEditingId(null); setEditValue(''); } }}
                              autoFocus
                              className="w-12 border-0 bg-transparent text-center text-sm font-medium outline-none focus:ring-1 focus:ring-[#5a6552]"
                            />
                          ) : (
                            <button onClick={() => startEdit(item)} className="min-w-8 text-center text-sm font-medium hover:text-[#5a6552]">{item.quantity}</button>
                          )}
                          <button onClick={() => updateQuantity(item.id, item.quantity + 1)} aria-label="Aantal verhogen" className="p-2 text-[#5a6552] transition-colors hover:text-[#1e2b22]"><Plus className="h-3.5 w-3.5" /></button>
                        </div>
                        <button onClick={() => removeItem(item.id)} aria-label={`${item.product_name} verwijderen`} className="text-[#8b867d] transition-colors hover:text-[#a47b21] sm:hidden"><Trash2 className="h-4 w-4" /></button>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end">
                        <span className="text-xs text-[#8b867d] sm:hidden">Totaal</span>
                        <div className="flex items-center gap-3">
                          <p className="text-sm font-semibold text-[#1e2b22] sm:text-base">{formatPrice(item.unit_price * item.quantity)}</p>
                          <button onClick={() => removeItem(item.id)} aria-label={`${item.product_name} verwijderen`} className="hidden text-[#8b867d] transition-colors hover:text-[#a47b21] sm:block"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>

                <div className="flex justify-end border-b border-[#e7e1d6] py-5">
                  <p className="text-sm text-[#5a6552]">Kortingscode toepassen tijdens het <button onClick={() => navigate('/winkelwagen/afrekenen')} className="font-medium text-[#1e2b22] underline-offset-2 hover:underline">afrekenen</button></p>
                </div>

                <div className="ml-auto max-w-[420px] pt-6">
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between"><span className="text-[#5a6552]">Subtotaal</span><span className="font-medium">{formatPrice(total)}</span></div>
                    {totalDiscount > 0 && <div className="flex justify-between"><span className="text-[#5a6552]">Staffelkorting</span><span className="font-medium text-[#5a6552]">- {formatPrice(totalDiscount)}</span></div>}
                    {deliverySettings ? (
                      <div className="flex justify-between">
                        <span className="text-[#5a6552] flex items-center gap-1.5"><Truck className="h-3.5 w-3.5" />Bezorgkosten</span>
                        <span className="font-medium text-[#1e2b22]">{transportIsFree ? 'Gratis' : formatPrice(deliveryFee)}</span>
                      </div>
                    ) : (
                      <div className="flex justify-between">
                        <span className="text-[#5a6552] flex items-center gap-1.5"><Truck className="h-3.5 w-3.5" />Bezorgkosten</span>
                        <span className="font-medium text-[#8b867d]">Wordt berekend bij afrekenen</span>
                      </div>
                    )}
                    {!transportIsFree && deliverySettings && (
                      <p className="text-xs text-[#8b867d]">Gratis bezorging vanaf {formatPrice(deliverySettings.free_from_amount_cents / 100)}</p>
                    )}
                    <div className="flex items-end justify-between border-t border-[#e7e1d6] pt-5"><div><p className="text-xs uppercase tracking-[0.16em] text-[#8b867d]">Totaal</p><p className="mt-1 text-xs text-[#8b867d]">Inclusief BTW en bezorging</p></div><strong className="text-2xl font-semibold">{formatPrice(grandTotal)}</strong></div>
                  </div>
                  <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <button onClick={() => navigate('/assortiment')} className="inline-flex min-h-11 items-center justify-center rounded-full border border-[#5a6552] px-6 text-sm font-medium text-[#1e2b22] transition-colors hover:bg-white">Verder winkelen</button>
                    <button onClick={() => navigate('/winkelwagen/afrekenen')} className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-[#1e2b22] px-7 text-sm font-semibold text-white transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-[#5a6552]">Afrekenen <ArrowRight className="h-4 w-4" /></button>
                  </div>
                </div>
              </section>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
